import { MetadataAttribute } from '@ariksa/inventory-core/api';
import dayjs from 'dayjs';
import { groupBy, isArray, isBoolean, isPlainObject, isString } from 'lodash';

// Presentation settings from ui_metadata (cloud-resources); newer than the
// generated client, so typed here until it is regenerated. The client also
// types attribute_value as links only, but it carries any field value.
export interface DynamicAttribute
  extends Omit<MetadataAttribute, 'attribute_value'> {
  attribute_value?: any;
  section?: string;
  check?: boolean;
  rule?: string;
  header?: boolean;
  warn_within_days?: number;
  warn_after_days?: number;
  unavailable_reason?: string;
}

export const DEFAULT_SECTION = 'Overview';

export type CheckStatus = 'pass' | 'fail' | 'unknown' | 'unavailable';
export type DateWarning = 'expired' | 'soon' | 'stale' | undefined;

export const isEmptyValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  (isString(value) && ['', '-', 'N/A', 'None'].includes(value.trim())) ||
  (isArray(value) && value.length === 0) ||
  (isPlainObject(value) && Object.keys(value as object).length === 0);

// The API sends booleans as 'True' / 'False' strings.
export const toBoolean = (value: unknown): boolean | undefined => {
  if (isBoolean(value)) return value;
  if (isString(value)) {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return undefined;
};

export const checkStatus = (attribute: DynamicAttribute): CheckStatus => {
  if (attribute.unavailable_reason) return 'unavailable';
  const value = toBoolean(attribute.attribute_value);
  if (value === undefined) return 'unknown';
  return value === (attribute.check ?? true) ? 'pass' : 'fail';
};

export const isCheck = (attribute: DynamicAttribute) =>
  attribute.check !== undefined && attribute.check !== null;

export const dateWarning = (
  attribute: DynamicAttribute,
  now = dayjs(),
): DateWarning => {
  const {
    attribute_value: value,
    warn_within_days,
    warn_after_days,
  } = attribute;
  if (!isString(value) || isEmptyValue(value)) return undefined;
  const date = dayjs(value);
  if (!date.isValid()) return undefined;
  if (warn_within_days !== undefined && warn_within_days !== null) {
    if (date.isBefore(now)) return 'expired';
    if (date.diff(now, 'day') <= warn_within_days) return 'soon';
  }
  if (warn_after_days !== undefined && warn_after_days !== null) {
    if (now.diff(date, 'day') > warn_after_days) return 'stale';
  }
  return undefined;
};

export const isUrl = (value: unknown): value is string =>
  isString(value) && /^https?:\/\/\S+$/i.test(value.trim());

export interface SummarySection {
  name: string;
  checks: DynamicAttribute[];
  fields: DynamicAttribute[];
  empty: DynamicAttribute[];
  failing: number;
}

// Overview first, Security checks last, others in first-seen order.
const sectionRank = (name: string) =>
  name === DEFAULT_SECTION ? -1 : name === 'Security checks' ? 1 : 0;

export const buildSections = (
  attributes: DynamicAttribute[],
): SummarySection[] => {
  const grouped = groupBy(attributes, a => a.section || DEFAULT_SECTION);
  return Object.entries(grouped)
    .map(([name, items]) => {
      const checks = items.filter(isCheck);
      const others = items.filter(a => !isCheck(a));
      return {
        name,
        checks,
        fields: others.filter(a => !isEmptyValue(a.attribute_value)),
        empty: others.filter(a => isEmptyValue(a.attribute_value)),
        failing: checks.filter(a => checkStatus(a) === 'fail').length,
      };
    })
    .filter(s => s.checks.length || s.fields.length || s.empty.length)
    .sort((a, b) => sectionRank(a.name) - sectionRank(b.name));
};

export const sectionSummary = (section: SummarySection) => {
  if (!section.checks.length) return `${section.fields.length}`;
  if (section.failing) return `${section.failing} failing`;
  const passing = section.checks.filter(c => checkStatus(c) === 'pass').length;
  return `${passing}/${section.checks.length} passing`;
};
